import {
  BufferGeometry,
  Camera,
  Curve,
  CylinderGeometry,
  Mesh,
  MeshBasicMaterial,
  OrthographicCamera,
  Scene,
  TubeGeometry,
  Vector3,
  WebGLRenderer,
} from "three";
import { Quick3DPerspectiveCamera } from "../camera/quick3d-perspective-camera";
import * as Config from "../config/ViewerConfig";

class CustomCurve extends Curve<Vector3> {
  private _points: Vector3[];
  constructor(points: Vector3[]) {
    super();
    this._points = points;
  }

  getPoint(t: number, optionalTarget = new Vector3()) {
    if (this._points.length < 3) return optionalTarget.set(0, 0, 0);
    const maxIndex = this._points.length - 1;

    const startIndex = Math.floor(t * (maxIndex - 1));
    const decimal = t === 1 ? 1 : t * (maxIndex - 1) - startIndex;
    const endIndex = startIndex + 1;
    const start = this._points[startIndex];
    const end = this._points[endIndex];

    const tx = start.x + (end.x - start.x) * decimal;
    const ty = start.y + (end.y - start.y) * decimal;
    const tz = start.z + (end.z - start.z) * decimal;

    return optionalTarget.set(tx, ty, tz);
  }

  public setPoints(points: Vector3[]) {
    this._points = points;
  }
}

export class AutoAttenuableTube extends Mesh<
  BufferGeometry,
  MeshBasicMaterial
> {
  private _points: Vector3[];
  private _path: CustomCurve;
  private _defaultMaterial: MeshBasicMaterial;
  private _selectedMaterial: MeshBasicMaterial;
  private _isSelected: boolean;

  get points() {
    return this._points;
  }
  constructor(
    points: Vector3[],
    material = new MeshBasicMaterial({
      color: 0x0000ff,
      depthTest: false,
    })
  ) {
    const path = new CustomCurve(points);
    const geometry = new TubeGeometry(path, points.length, 0.01, 8, false);
    super(geometry, material);
    this._defaultMaterial = material;
    this._selectedMaterial = new MeshBasicMaterial({
      color: Config.SELECTED_MEASUREMENT_COLOR,
      depthTest: false,
      transparent: true,
    });
    this._points = points;
    this._path = path;

    this.onBeforeRender = function (
      _ifcRenderer: WebGLRenderer,
      _nonClippingScene: Scene,
      camera: Camera
    ): void {
      const windowScale = Math.sqrt(
        (1280 * 720) / (window.screen.width * window.screen.height)
      );
      const distanceToNear = Math.min(
        ...this._points.map((pos) => pos.distanceTo(camera.position))
      );
      if (camera instanceof OrthographicCamera) {
        const geometry = new TubeGeometry(
          this._path,
          this._points.length,
          ((camera.right - camera.left) / camera.zoom) *
            (this._isSelected
              ? Config.SELECTED_TUBE_CONSTANT_ORTHOGRAPHIC
              : Config.DEFAULT_TUBE_CONSTANT_ORTHOGRAPHIC) *
            windowScale,
          8,
          false
        );
        this.geometry = geometry;
      }
      if (camera instanceof Quick3DPerspectiveCamera) {
        const geometry = new TubeGeometry(
          this._path,
          this._points.length,
          distanceToNear *
            (this._isSelected
              ? Config.SELECTED_TUBE_CONSTANT_PERSPECTIVE
              : Config.DEFAULT_TUBE_CONSTANT_PERSPECTIVE) *
            windowScale,
          8,
          false
        );
        this.geometry = geometry;
      }
      this.updateMatrixWorld();
    };
  }

  public updateLength(length: number) {
    this.geometry = new CylinderGeometry(
      0.125,
      0.125,
      length,
      8,
      1,
      true
    );
    this.geometry.lookAt(new Vector3(0, 1, 0));
  }

  public updatePoints(points: Vector3[]) {
    if (points.length === 2) {
      const start = new Vector3().copy(points[0]);
      const end = new Vector3().copy(points[1]);
      const middle = start.add(end).divideScalar(2);
      points.push(middle);
    }
    this._points = points;
    this._path.setPoints(points);
    this._path = new CustomCurve(points);
    const geometry = new TubeGeometry(
      this._path,
      points.length,
      0.01,
      8,
      false
    );
    this.geometry = geometry;
  }

  public dispose(): void {
    if (this.parent) {
      this.parent.remove(this);
    }
    this.geometry?.dispose();
    if (Array.isArray(this.material)) {
      for (const m of this.material) {
        m.dispose();
      }
    } else {
      this.material?.dispose();
    }
    this.geometry = undefined as unknown as BufferGeometry;
    this.material = undefined as unknown as MeshBasicMaterial;
  }

  public setSelectedMaterial(isSelected: boolean) {
    this.material = isSelected ? this._selectedMaterial : this._defaultMaterial;
  }

  public setSelectedGeometry(isSelected: boolean) {
    this._isSelected = isSelected;
  }

  public changeRenderOrderBySelected(isSelected: boolean) {
    this.renderOrder = isSelected ? 2 : 0;
  }
}
